/**
 * Billing-freeze paywall bus (portal SoT 2026-09-29).
 *
 * Frozen = read OK; every merchant write → open Pay outstanding invoice.
 * Nest message must stay in sync with billing-freeze.service.ts FREEZE_BLOCK_MESSAGE.
 */

export const BILLING_FREEZE_BLOCK_MESSAGE =
  "This account is temporarily frozen due to a billing issue. Please update payment in Billing, or contact support.";

type PaywallApi = {
  isFrozen: () => boolean;
  open: () => void;
};

let api: PaywallApi = {
  isFrozen: () => false,
  open: () => undefined,
};

/**
 * @param {PaywallApi} next - Host registers Redux frozen flag + modal opener
 * @returns {() => void} Unregister
 */
export function registerBillingFrozenPaywall(next: PaywallApi): () => void {
  api = next;
  return () => {
    api = {
      isFrozen: () => false,
      open: () => undefined,
    };
  };
}

/**
 * Before a create/update entry: if frozen, open paywall and return false.
 * @returns {boolean} True when the write may proceed
 */
export function assertBillingWritable(): boolean {
  if (!api.isFrozen()) return true;
  api.open();
  return false;
}

/**
 * @param {unknown} error - Axios or Nest error body
 * @returns {boolean} True when the response is the freeze gate
 */
export function isBillingFreezeApiError(error: unknown): boolean {
  const data = (error as { response?: { data?: unknown; status?: number } })
    ?.response?.data;
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status !== 400) return false;
  const message = extractErrorMessage(data);
  if (!message) return false;
  return (
    message.includes("temporarily frozen due to a billing issue") ||
    message.includes("BILLING_FROZEN_USE_CUSTOMER_PORTAL")
  );
}

/**
 * Opens the paywall when Nest already blocked a write (axios funnel).
 * @param {unknown} error
 * @returns {boolean} True when handled as freeze
 */
export function maybeOpenPaywallFromApiError(error: unknown): boolean {
  if (!isBillingFreezeApiError(error)) return false;
  api.open();
  return true;
}

function extractErrorMessage(data: unknown): string {
  if (typeof data === "string") return data;
  if (!data || typeof data !== "object") return "";
  const obj = data as Record<string, unknown>;
  if (typeof obj.message === "string") return obj.message;
  if (obj.message && typeof obj.message === "object") {
    const nested = obj.message as Record<string, unknown>;
    if (typeof nested.message === "string") return nested.message;
  }
  if (typeof obj.error === "string") return obj.error;
  if (typeof obj.code === "string" && obj.code.includes("BILLING_FROZEN")) {
    return obj.code;
  }
  return "";
}
